import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  Calendar,
  Crown,
  Shield,
  Video,
} from 'lucide-react';

/**
 * Compact attention / onboarding notices for the vendor dashboard.
 * Renders nothing when there is nothing actionable.
 */
const VendorAttentionStack = ({
  isVendorVerified,
  isVerificationPending,
  onStartVerification,
  hasActiveSubscription,
  subscriptionDetails,
  isExpiringSoon,
  urgencyLevel,
  onRenew,
  onComparePlans,
  pendingConsultationCount,
  onViewConsultations,
  fiveDayReminderBookings,
  eventReminderByDays,
  onOpenReminder,
  onViewBookings,
}) => {
  const items = [];

  if (!isVendorVerified && !isVerificationPending) {
    items.push({
      key: 'verify',
      tone: 'sky',
      Icon: Shield,
      title: 'Complete vendor verification',
      body: 'Submit your business details to unlock full vendor features.',
      primary: { label: 'Start verification', onClick: onStartVerification },
    });
  }

  if (!isVendorVerified && isVerificationPending) {
    items.push({
      key: 'pending-verify',
      tone: 'amber',
      Icon: Shield,
      title: 'Verification under review',
      body: 'Your submission is pending admin approval. We’ll notify you once it’s approved.',
    });
  }

  if (hasActiveSubscription && subscriptionDetails?.is_free_tier) {
    items.push({
      key: 'free',
      tone: 'amber',
      Icon: Crown,
      title: 'You’re on the Free plan',
      body: 'Upgrade for higher limits and premium features like advanced analytics and branding.',
      primary: { label: 'Compare plans', onClick: onComparePlans, href: '/vendor/subscription' },
    });
  }

  if (hasActiveSubscription && subscriptionDetails && isExpiringSoon) {
    const days = subscriptionDetails.days_remaining;
    const critical = urgencyLevel === 'critical';
    items.push({
      key: 'expiring',
      tone: critical ? 'rose' : 'amber',
      Icon: Bell,
      title: critical
        ? 'Subscription expires tomorrow'
        : `Subscription expires in ${days} day${days !== 1 ? 's' : ''}`,
      body: subscriptionDetails.is_free_tier
        ? 'Choose a paid plan to keep premium access without interruption.'
        : 'Renew now to avoid service interruption.',
      primary: {
        label: subscriptionDetails.is_free_tier ? 'Choose a paid plan' : 'Renew subscription',
        onClick: onRenew,
      },
    });
  }

  if (pendingConsultationCount > 0) {
    items.push({
      key: 'consult',
      tone: 'amber',
      Icon: Video,
      title: `${pendingConsultationCount} consultation request${pendingConsultationCount !== 1 ? 's' : ''} awaiting response`,
      body: 'Accept or decline so clients know you’re available.',
      primary: { label: 'View consultations', onClick: onViewConsultations },
    });
  }

  if (fiveDayReminderBookings?.length > 0) {
    const dayBits = [0, 1, 2, 3, 4, 5]
      .map((days) => {
        const list = eventReminderByDays?.[days] || [];
        if (!list.length) return null;
        const label = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days}d`;
        return `${label}: ${list.length}`;
      })
      .filter(Boolean)
      .join(' · ');

    items.push({
      key: 'events',
      tone: 'sky',
      Icon: Calendar,
      title: `${fiveDayReminderBookings.length} upcoming event${fiveDayReminderBookings.length !== 1 ? 's' : ''} in the next 5 days`,
      body: dayBits || 'Review schedules and prepare for the week ahead.',
      primary: { label: 'Open reminder', onClick: onOpenReminder },
      secondary: { label: 'View bookings', onClick: onViewBookings },
    });
  }

  if (items.length === 0) return null;

  const tones = {
    sky: {
      wrap: 'border-sky-200 dark:border-sky-800/50 bg-sky-50/80 dark:bg-sky-950/30',
      icon: 'bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300',
      title: 'text-sky-950 dark:text-sky-100',
      body: 'text-sky-800/90 dark:text-sky-300',
      btn: 'bg-sky-700 hover:bg-sky-800 text-white',
      btnGhost:
        'border border-sky-300 dark:border-sky-700 text-sky-800 dark:text-sky-200 hover:bg-sky-100/80 dark:hover:bg-sky-900/40',
    },
    amber: {
      wrap: 'border-amber-200 dark:border-amber-800/50 bg-amber-50/80 dark:bg-amber-950/30',
      icon: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300',
      title: 'text-amber-950 dark:text-amber-100',
      body: 'text-amber-800/90 dark:text-amber-300',
      btn: 'bg-amber-800 hover:bg-amber-900 text-white',
      btnGhost:
        'border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 hover:bg-amber-100/80 dark:hover:bg-amber-900/40',
    },
    rose: {
      wrap: 'border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30',
      icon: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
      title: 'text-red-950 dark:text-red-100',
      body: 'text-red-800/90 dark:text-red-300',
      btn: 'bg-red-700 hover:bg-red-800 text-white',
      btnGhost:
        'border border-red-300 dark:border-red-700 text-red-900 dark:text-red-200 hover:bg-red-100/80 dark:hover:bg-red-900/40',
    },
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-0.5">
        Needs attention
      </p>
      <div className="space-y-2.5">
        {items.map((item) => {
          const t = tones[item.tone] || tones.amber;
          const Icon = item.Icon;
          return (
            <div
              key={item.key}
              className={`rounded-2xl border p-4 ${t.wrap}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                <div className={`p-2 rounded-xl shrink-0 w-fit ${t.icon}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className={`text-sm font-semibold ${t.title}`}>{item.title}</h3>
                  <p className={`text-sm mt-1 leading-relaxed ${t.body}`}>{item.body}</p>
                  {(item.primary || item.secondary) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {item.primary?.href ? (
                        <Link
                          to={item.primary.href}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium !rounded-xl ${t.btn}`}
                        >
                          {item.primary.label}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      ) : item.primary ? (
                        <button
                          type="button"
                          onClick={item.primary.onClick}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium !rounded-xl ${t.btn}`}
                        >
                          {item.primary.label}
                        </button>
                      ) : null}
                      {item.secondary ? (
                        <button
                          type="button"
                          onClick={item.secondary.onClick}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium !rounded-xl bg-white/70 dark:bg-gray-900/40 ${t.btnGhost}`}
                        >
                          {item.secondary.label}
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VendorAttentionStack;
